<template>
  <panel-layout
    id="reservation-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="reservation" class="flex justify-between items-center">
        <div>
          <h3 class="text-xl tracking-tight font-bold leading-none mr-3">
            {{ heading }}
          </h3>

          <p class="opacity-80 mt-1 mb-1">
            {{ reservation.dateLabel }}
          </p>
        </div>
      </div>
    </template>

    <template #actions-left>
      <template v-if="reservation">
        <template v-if="reservation.isGroup">
          <o-button
            outline
            size="sm"
            class="mb-1 mr-1"
            icon="customerAdd"
            @click="handleAddCustomer"
          >
            {{ $t('components.reservation.edit.attendees.addCustomer') }}
          </o-button>
        </template>

        <div v-if="isDeleted">
          <o-button
            size="sm"
            variant="warning"
            class="mb-1 mr-1"
            @click="handleRestore"
          >
            {{ $t('reservation.restore') }}
          </o-button>
        </div>

        <div v-if="allowActions" class="hidden md:flex items-center flex-wrap">
          <!-- Confirm -->
          <!--If the appointment is unconfirmed (was booked online), show the Approve / Cancel buttons-->
          <template v-if="!isGroup && isUnconfirmed && isBeforeStart && reservation.allow.confirm">
            <o-button
              v-if="reservation.allow.confirm"
              size="sm"
              variant="success"
              icon="reservationConfirm"
              class="mb-1 mr-1"
              @click="handleConfirm"
            >
              {{ $t('general.confirm') }}
            </o-button>
          </template>

          <!-- Cancel button moved to dropdown menu -->

          <template v-if="isCancelled">
            <o-button
              v-if="reservation.allow.cancel"
              size="sm"
              outline
              icon="reservationCancel"
              class="mb-1 mr-1"
              @click="handleUndoCancel"
            >
              {{ $t('reservation.undoCancel') }}
            </o-button>
          </template>

          <!--If the current time is prior to the appointment time and prior to the reminder notification window, show the Move button.-->
          <o-panel-link
            v-if="isBeforeStart && isBeforeReminder && reservation.allow.move"
            tag="o-button"
            size="sm"
            path="/appointment/create"
            :props="{ presets: { move: reservation } }"
            outline
            icon="reservationMove"
            class="mb-1 mr-1"
          >
            {{ $t('reservation.move') }}
          </o-panel-link>

          <template v-if="!isGroup">
            <!--If the current time is prior to the appointment time but after the reminder notification window (of reminder time is null), show the Check-in button.-->
            <!--If the current time is after to the appointment time and not checked in, show the Check-in button and the No-show button.-->
            <template v-if="((isBeforeStart && !isBeforeReminder) || !isBeforeStart) && !isCheckedIn && !isUnconfirmed && !isCancelled && !isNoShow">
              <o-button
                v-if="reservation.allow.checkin"
                size="sm"
                outline
                icon="reservationCheckIn"
                class="mb-1 mr-1"
                @click="handleCheckIn"
              >
                {{ $t('general.checkIn') }}
              </o-button>
            </template>

            <template v-if="!isBeforeStart && !isCheckedIn && !isCancelled && !isNoShow">
              <o-button
                v-if="reservation.allow.noShow"
                size="sm"
                outline
                icon="reservationNoShow"
                class="mb-1 mr-1"
                @click="handleNoShow"
              >
                {{ $t('reservation.noShow') }}
              </o-button>
            </template>

            <!--If the current time is after the appointment time, show the 'Checkout' button. Also show the Re-book button-->
            <template v-if="!isBeforeStart">
              <o-button
                v-if="(!hasOrder && !isPaid) && $grants.all({ order: ['create'] })"
                size="sm"
                outline
                icon="sale"
                class="mb-1 mr-1"
                @click="handleCreateOrder"
              >
                {{ $t('reservation.checkoutNewSale') }}
              </o-button>

              <o-panel-link
                v-if="hasOrder && $grants.all({ order: ['view'] })"
                size="sm"
                outline
                icon="sale"
                class="mb-1 mr-1"
                tag="o-button"
                :path="`/order/${reservation.order.idOrder}`"
              >
                {{ $t('order.view') }}
              </o-panel-link>
            </template>
          </template>
        </div>
      </template>
    </template>

    <template #actions-right>
      <div v-if="reservation && allowActions" class="flex items-center space-x-1">
        <!-- Standard Schedule -->
        <template v-if="reservation.isStandardSchedule">
          <!-- Move reservation -->
          <o-tooltip v-if="reservation.allow.move" class="h-8" :text="$t('reservation.moveAppointment')">
            <o-panel-link
              tag="o-button"
              path="/appointment/create"
              :props="{ presets: { move: reservation } }"
              flat
              icon="reservationMove"
            />
          </o-tooltip>

          <template v-if="!reservation.isGroup">
            <!-- View Sale -->
            <o-tooltip v-if="hasOrder && $grants.all({ order: ['view'] })" class="h-8" :text="$t('order.view')">
              <o-panel-link
                tag="o-button"
                size="slim"
                flat
                icon="sale"
                :path="`/order/${reservation.order.idOrder}`"
              />
            </o-tooltip>

            <o-tooltip v-if="(!hasOrder && !isPaid) && $grants.all({ order: ['create'] })" class="h-8" :text="$t('reservation.checkout')">
              <o-button
                size="slim"
                flat
                icon="sale"
                @click="handleCreateOrder"
              />
            </o-tooltip>
          </template>

          <!-- Rebook -->
          <o-tooltip v-if="!isGroup && !reservation.customer?.isArchived && reservation.allow.create" class="h-8" :text="$t('reservation.rebook')">
            <o-button
              size="slim"
              flat
              icon="reservationRebook"
              @click="handleRebook"
            />
          </o-tooltip>
        </template>

        <!-- Recurring -->
        <o-tooltip v-if="!reservation.customer?.isArchived && !reservation.recurrenceGuid && reservation.allow.create && reservation.allow.edit" class="h-8" :text="$t('reservation.makeRecurringReservation')">
          <o-button
            size="slim"
            flat
            icon="reservationRecurrence"
            @click="handleRecurring"
          />
        </o-tooltip>

        <!-- More -->
        <o-dropdown
          placement="bottom-end"
        >
          <o-button
            size="slim"
            flat
            icon="ellipses"
          />


          <template #content>
            <template v-if="!isGroup">
              <template v-if="isUnconfirmed && reservation.allow.confirm">
                <o-dropdown-item v-if="reservation.allow.confirm" icon="reservationConfirm" variant="success" @click="handleConfirm">
                  {{ $t('general.confirm') }}
                </o-dropdown-item>

                <o-dropdown-divider />
              </template>

              <o-dropdown-item v-if="!isCheckedIn && reservation.allow.checkin" icon="reservationCheckIn" @click="handleCheckIn">
                {{ $t('reservation.checkIn') }}
              </o-dropdown-item>
              <o-dropdown-item v-if="!isNoShow && reservation.allow.noShow" icon="reservationNoShow" @click="handleNoShow">
                {{ $t('reservation.noShow') }}
              </o-dropdown-item>

              <o-dropdown-item v-if="isCheckedIn && reservation.allow.checkin" icon="reservationCheckIn" @click="handleUndoCheckIn">
                {{ $t('reservation.undoCheckIn') }}
              </o-dropdown-item>
              <o-dropdown-item v-if="isNoShow && reservation.allow.noShow" icon="reservationNoShow" @click="handleUndoNoShow">
                {{ $t('reservation.undoNoShow') }}
              </o-dropdown-item>

              <o-dropdown-divider />

              <o-dropdown-item v-if="(!hasOrder && !isPaid) && $grants.all({ order: ['create'] })" icon="sale" @click="handleCreateOrder">
                {{ $t('reservation.checkoutNewSale') }}
              </o-dropdown-item>

              <o-dropdown-item v-if="hasOrder && $grants.all({ order: ['view'] })" icon="sale" tag-name="o-panel-link" :path="`/order/${reservation.order.idOrder}`">
                {{ $t('order.view') }}
              </o-dropdown-item>

              <o-dropdown-item v-if="isSingle" icon="print" @click="handlePrint">
                {{ $t('general.print') }}
              </o-dropdown-item>
            </template>

            <template v-if="integrationsWithActions && reservation.allow.edit">
              <o-dropdown-submenu v-for="integration in integrationsWithActions" :key="integration.integration.guid" :icon="integration.integration.icon || 'integrations'">
                {{ integration.integration.name }}

                <template #content>
                  <o-dropdown-item v-for="action in integration.actions" :key="action.code">
                    <span @click="callIntegrationAction(integration.integration, action)">
                      {{ action.title }}
                    </span>
                  </o-dropdown-item>
                </template>
              </o-dropdown-submenu>

              <o-dropdown-divider />
            </template>

            <o-dropdown-submenu v-if="reservation.allow.email" icon="email">
              {{ $t('general.sendEmail') }}

              <template #content>
                <template v-if="isGroup">
                  <o-panel-link
                    :props="messageProps"
                    tag="o-dropdown-item"
                    path="/message/send/email"
                    :description="$t('pages.appointment.id.customEmail')"
                  >
                    {{ $t('pages.appointment.id.emailAppointmentCustomers') }}
                  </o-panel-link>

                  <o-panel-link
                    :props="messageProps"
                    tag="o-dropdown-item"
                    path="/message/send/sms"
                    :description="$t('pages.appointment.id.customSMS')"
                  >
                    {{ $t('pages.appointment.id.smsAppointmentCustomers') }}
                  </o-panel-link>

                  <o-dropdown-divider />
                </template>

                <o-dropdown-item :description="$t('reservation.appointmentReceived')" @click="handleSendEmail(ReservationEmailType.received)">
                  {{ $t('pages.appointment.id.appointmentReceived') }}
                </o-dropdown-item>
                <o-dropdown-item :description="$t('reservation.appointmentConfirmation')" @click="handleSendEmail(ReservationEmailType.confirmation)">
                  {{ $t('general.confirmation') }}
                </o-dropdown-item>
                <o-dropdown-item :description="$t('reservation.appointmentReminder')" @click="handleSendEmail(ReservationEmailType.reminderEmail)">
                  {{ $t('reservation.reminderEmail') }}
                </o-dropdown-item>
                <o-dropdown-item :description="$t('reservation.appointmentReminderSMS')" @click="handleSendEmail(ReservationEmailType.reminderSMS)">
                  {{ $t('reservation.reminderSMS') }}
                </o-dropdown-item>
                <o-dropdown-item :disabled="!isAfterStart" :description="$t('reservation.reviewRequestInfo')" @click="handleSendEmail(ReservationEmailType.reviewRequest)">
                  {{ $t('reservation.reviewRequest') }}
                </o-dropdown-item>
              </template>
            </o-dropdown-submenu>

            <o-panel-link
              :props="{ presets: { move: reservation } }"
              tag="o-dropdown-item"
              path="/appointment/create"
              icon="reservationMove"
            >
              {{ $t('reservation.move') }}
            </o-panel-link>

            <o-dropdown-item v-if="!isCancelled && reservation.allow.cancel" icon="reservationCancel" variant="danger" @click="handleCancel">
              {{ $t('general.cancel') }}
            </o-dropdown-item>

            <o-dropdown-item v-if="isCancelled && reservation.allow.cancel && reservation.allow.edit" icon="reservationCancel" variant="danger" @click="handleUndoCancel">
              {{ $t('reservation.undoCancel') }}
            </o-dropdown-item>

            <template v-if="!isGroup && !reservation.customer?.isArchived && reservation.allow.create && reservation.allow.edit">
              <o-dropdown-item icon="reservationRebook" @click="handleRebook">
                {{ $t('reservation.rebook') }}
              </o-dropdown-item>
            </template>

            <o-dropdown-item v-if="!reservation.customer?.isArchived" icon="reservationRecurrence" @click="handleRecurring">
              {{ $t('reservation.recurring') }}
            </o-dropdown-item>

            <o-history-link :item="reservation" model="Reservation" component="ODropdownItem" icon="history" />

            <o-dropdown-divider />

            <o-dropdown-item v-if="reservation.allow.delete" icon="delete" variant="danger" @click="handleDelete">
              {{ $t('general.delete') }}
            </o-dropdown-item>
          </template>
        </o-dropdown>
      </div>
    </template>

    <div v-if="reservation" class="h-full dark:bg-gray-900">
      <o-scroll class="h-full">
        <div class="h-full flex flex-col">
          <div class="pb-8">
            <info v-model:reservation="reservation" v-model:loading="loading" @update="handleUpdate" @recurring="handleRecurring" />
          </div>
          <div class="w-full flex-1 min-h-xs">
            <o-tabs nav-class="px-5 border-b dark:border-gray-800 dark:/0" container-class="py-5 bg-gray-50/0 dark: h-full" scroll>
              <o-tab v-if="isGroup" :name="$t('general.customers')" icon="attendees">
                <attendees v-model:reservation="reservation" v-model:loading="loading" @update="handleUpdate" />
              </o-tab>
              <o-tab :name="isGroup ? 'Service' : 'Services'" icon="services">
                <services v-model:reservation="reservation" />
              </o-tab>
              <o-tab v-if="$features.get('custom-fields') && reservation.customFields && reservation.customFields.length" :name="$t('general.customFields')" icon="customFields">
                <fields :reservation="reservation" />
              </o-tab>
              <o-tab v-if="$features.get('forms') && ((reservation.forms && reservation.forms.length) || (reservation.relatedForms && reservation.relatedForms.length))" :alert="incompleteFormCount" :name="$t('general.forms')" icon="forms">
                <forms :reservation="reservation" />
              </o-tab>
              <o-tab v-if="$features.get('service-automation') && isSingle && hasAutomations" :name="$t('general.aftercare')" icon="sparkles">
                <aftercare v-model:reservation="reservation" v-model:loading="loading" @update="handleUpdate" />
              </o-tab>
              <o-tab :name="$t('general.uploads')" icon="upload" :alert="uploadCount">
                <uploads :reservation="reservation" @update:count="uploadCount = $event" />
              </o-tab>
            </o-tabs>
          </div>
        </div>
      </o-scroll>
    </div>

    <div v-if="loadError" class="h-full flex flex-col items-center justify-center">
      <p class="text-gray-500">There was an error loading the appointment.</p>
    </div>

    <template #footer>
      <div v-if="reservation && reservation.allow.edit" class="flex items-center justify-end">
        <div v-if="!isRecurring" class="flex items-center gap-2">
          <o-button v-if="reservation.allow.edit" variant="primary" icon="save" :disabled="disabled" @click="save()">
            {{ $t('general.save') }}
          </o-button>
          <o-button v-if="reservation.allow.edit" variant="primary" icon="save" :disabled="disabled" @click="save(true)">
            {{ $t('general.saveAndClose') }}
          </o-button>
        </div>

        <div v-if="isRecurring" class="flex items-center gap-2">
          <o-dropdown v-if="reservation.allow.edit">
            <o-button variant="primary" icon="save" :disabled="disabled">
              {{ $t('general.save') }}
            </o-button>

            <template #content>
              <o-dropdown-item v-if="reservation.allow.edit" variant="primary" :disabled="disabled" @click="save(false, false)">
                {{ $t('pages.appointment.id.justThis') }}
              </o-dropdown-item>
              <hr class="my-1">
              <o-dropdown-item v-if="reservation.allow.edit" variant="primary" :disabled="disabled" @click="save(false, true)">
                {{ $t('pages.appointment.id.allFuture') }}
              </o-dropdown-item>
            </template>
          </o-dropdown>

          <o-dropdown v-if="reservation.allow.edit">
            <o-button variant="primary" icon="save" :disabled="disabled">
              {{ $t('general.saveAndClose') }}
            </o-button>

            <template #content>
              <o-dropdown-item v-if="reservation.allow.edit" variant="primary" :disabled="disabled" @click="save(true, false)">
                {{ $t('pages.appointment.id.justThis') }}
              </o-dropdown-item>
              <o-dropdown-item v-if="reservation.allow.edit" variant="primary" :disabled="disabled" @click="save(true, true)">
                {{ $t('pages.appointment.id.allFuture') }}
              </o-dropdown-item>
            </template>
          </o-dropdown>
        </div>
      </div>
    </template>

    <template v-if="reservation">
      <confirm-dialog
        ref="confirmDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
      />

      <check-in-dialog
        v-if="reservation.customer"
        ref="checkInDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
      />

      <undo-check-in-dialog
        v-if="reservation.customer"
        ref="undoCheckInDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
      />

      <cancel-dialog
        ref="cancelDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        :cancellation-types="reservation.options.cancellationTypes || []"
        v-model:loading="loading"
        @update="panel.close(true)"
      />

      <undo-cancel-dialog
        ref="undoCancelDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
      />

      <delete-dialog
        ref="deleteDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
        @success="cancel(true)"
      />

      <no-show-dialog
        ref="noShowDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
      />

      <undo-no-show-dialog
        v-if="reservation.customer"
        ref="undoNoShowDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
      />

      <send-email-dialog
        ref="sendEmailDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
      />

      <print-dialog
        ref="printDialog"
        :portal="panel.portal"
        v-model:loading="loading"
      />

      <add-attendee-dialog
        ref="addAttendeeDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        @update="handleUpdate"
      />

      <recurrence-modal
        ref="recurrenceModal"
        :portal="panel.portal"
        v-model:loading="loading"
        @success="() => fetchReservation(componentProps.id, componentProps.date)"
      />

      <rebook-dialog
        ref="rebookDialog"
        :portal="panel.portal"
        v-model:reservation="reservation"
        v-model:loading="loading"
      />
    </template>

    <unsaved-changes-dialog
      ref="unsavedChangesDialog"
      :portal="panel.portal"
    />

    <o-dialog
      ref="unsavedDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :portal="panel.portal"
      :options="[
        { text: $t('general.back'), type: 'cancel'},
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
    />

    <o-dialog
      ref="movedDialog"
      :title="$t('pages.appointment.id.attendeeUnavailable')"
      :info="$t('pages.appointment.id.attendeeUnavailableInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: 'Switch appointment', type: 'confirm', callback: handleMove },
      ]"
      :portal="panel.portal"
    />

    <o-dialog
      ref="overrideAvailabilityDialog"
      :title="$t('pages.appointment.id.overrideAvailability')"
      :info="$t('pages.appointment.id.overrideAvailabilityInfo')"
      :options="[
        { text: $t('general.cancel'), type: 'cancel' },
        { text: $t('pages.appointment.id.overrideAvailabilityConfirm'), type: 'confirm', callback: handleOverrideAvailability },
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import Info from '@/components/reservation/edit/Info.vue'
// @ts-ignore
import Services from '@/components/reservation/edit/Services.vue'
// @ts-ignore
import Fields from '@/components/reservation/edit/Fields.vue'
// @ts-ignore
import Attendees from '@/components/reservation/edit/Attendees.vue'
// @ts-ignore
import Forms from '@/components/reservation/edit/Forms.vue'
// @ts-ignore
import Uploads from '@/components/reservation/edit/Uploads.vue'
// @ts-ignore
import Preview from '@/components/reservation/edit/Preview.vue'
// @ts-ignore
import Aftercare from '@/components/reservation/edit/Aftercare.vue'

// @ts-ignore
import ConfirmDialog from '@/components/reservation/actions/Confirm.vue'
// @ts-ignore
import CancelDialog from '@/components/reservation/actions/Cancel.vue'
// @ts-ignore
import UndoCancelDialog from '@/components/reservation/actions/UndoCancel.vue'
// @ts-ignore
import CheckInDialog from '@/components/reservation/actions/CheckIn.vue'
// @ts-ignore
import UndoCheckInDialog from '@/components/reservation/actions/UndoCheckIn.vue'
// @ts-ignore
import DeleteDialog from '@/components/reservation/actions/Delete.vue'
// @ts-ignore
import NoShowDialog from '@/components/reservation/actions/NoShow.vue'
// @ts-ignore
import UndoNoShowDialog from '@/components/reservation/actions/UndoNoShow.vue'
// @ts-ignore
import SendEmailDialog from '@/components/reservation/actions/SendEmail.vue'
// @ts-ignore
import PrintDialog from '@/components/reservation/actions/Print.vue'
// @ts-ignore
import UnsavedChangesDialog from '@/components/reservation/actions/UnsavedChanges.vue'
// @ts-ignore
import RebookDialog from '@/components/reservation/actions/Rebook.vue'
// @ts-ignore
import AddAttendeeDialog from '@/components/reservation/actions/AttendeeAdd.vue'

// @ts-ignore
import RecurrenceModal from '@/components/reservation/actions/Recurrence.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'


// @ts-ignore
import {
  ODropdown,
  ODropdownItem,
  ODropdownDivider,
  ODropdownSubmenu,
  OTabs,
  OTab,
} from '@/components/elements'

import { ReservationEmailType } from '~/_api/models/reservation'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const componentProps = defineProps<{
  id?: string
  date?: string
  focused?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
  (e: 'update:reservation', value: any): void
}>()

const { $api, $bus, $notification, $auth, $date, $i18n } = useNuxtApp()
const { createOrder } = useOrderCreate()

const uid = Math.random().toString(36).slice(2)

// Template refs
const confirmDialog = ref<any>()
const checkInDialog = ref<any>()
const undoCheckInDialog = ref<any>()
const cancelDialog = ref<any>()
const undoCancelDialog = ref<any>()
const deleteDialog = ref<any>()
const noShowDialog = ref<any>()
const undoNoShowDialog = ref<any>()
const sendEmailDialog = ref<any>()
const printDialog = ref<any>()
const unsavedChangesDialog = ref<any>()
const unsavedDialog = ref<any>()
const movedDialog = ref<any>()
const recurrenceModal = ref<any>()
const rebookDialog = ref<any>()
const addAttendeeDialog = ref<any>()
const overrideAvailabilityDialog = ref<any>()

// Data
const reservation = ref<any>(null)
const errors = ref<any>(null)
const loadError = ref(false)
const loading = ref(true)
const moved = ref<any>(false)
const pendingSave = ref<{ close: boolean; future: boolean } | null>(null)
// ReservationEmailType imported directly from ~/_api/models/reservation

const provider = reactive({
  unsavedChanges: false,
  save,
})

provide('provider', provider)

// Computed
const heading = computed(() => {
  const r = reservation.value

  if (r && r.services) {
    return r.services
      .filter((service: any) => service && service.service)
      .map(({ service }: any) => service.title)
      .join(', ')
  }

  return 'Appointment'
})

const { unsavedChanges: hasChanges, snapshot } = useUnsavedChanges(reservation)
const unsavedChanges = computed(() => reservation.value?.allow?.edit && hasChanges.value)

const messageProps = computed(() => {
  const customers = reservation.value.groupCustomers.map(({ customer }: any) => customer)
  const template = ['Reservation', 'Customer', 'Location']
  const models = [reservation.value]

  return {
    customers,
    template,
    models,
  }
})

const integrationsWithActions = computed(() => {
  if (reservation.value.integrations) {
    return reservation.value.integrations.filter((integration: any) => integration.actions.length > 0)
  }

  return null
})

const disabled = computed(() => {
  return !reservation.value.services.length || (!reservation.value.customer && isSingle.value) || !unsavedChanges.value
})

const allowActions = computed(() => {
  return reservation.value && reservation.value.services.length && (isGroup.value || reservation.value.customer) && !isDeleted.value
})

const uploadCount = ref(0)

const incompleteFormCount = computed(() => {
  return reservation.value.forms
    ?.filter((form: any) => !form.isRequestStateCompleted)
    .length
})

const isRecurring = computed(() => reservation.value.isRecurring)
const isGroup = computed(() => reservation.value.isGroup)
const isSingle = computed(() => reservation.value.isSingle)
const isCancelled = computed(() => reservation.value.isStatusCancelled)
const isDeleted = computed(() => reservation.value?.deleted)
const isNoShow = computed(() => reservation.value.noShow)
const isUnconfirmed = computed(() => reservation.value.isStatusUnconfirmed)

const isBeforeStart = computed(() => {
  const nowTime = Number($date.getMinutes(new Date()))
  const startTime = Number(reservation.value.startTime.value)

  const nowDate = Number($date.format(new Date()))
  const startDate = Number(reservation.value.date.value)

  return nowDate <= startDate || (nowTime <= startTime && nowDate <= startDate)
})

const isAfterStart = computed(() => {
  const nowTime = Number($date.getMinutes(new Date()))
  const startTime = Number(reservation.value.startTime.value)

  const nowDate = Number($date.format(new Date()))
  const startDate = Number(reservation.value.date.value)

  return nowDate >= startDate || (nowTime >= startTime && nowDate >= startDate)
})

const isBeforeReminder = computed(() => {
  const utc = $date.utc(new Date())
  return $date.isBefore(utc, reservation.value.reminderTime * 1000)
})

const isCheckedIn = computed(() => reservation.value.checkedIn)
const isPaid = computed(() => reservation.value.isPaid)
const hasOrder = computed(() => !!reservation.value.order)
const hasAutomations = computed(() => reservation.value.automations && reservation.value.automations.length > 0)

// Watchers

watch(() => componentProps.focused, () => {
  handleFocused()
})

watch(unsavedChanges, (value) => {
  provider.unsavedChanges = value
})

// Methods
const getLocationTime = () => {
  const offset = $auth.info?.location.timeZoneOffset || 0
  const date = new Date(new Date().toUTCString().slice(0, -4))
  const time = $date.add(date, { seconds: offset })
  const minutes = (time as any).getMinutes()
  const hours = (time as any).getHours()

  return (hours * 60) + minutes
}

const handleCheckIn = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => checkInDialog.value.open() })
  } else {
    checkInDialog.value.open()
  }
}

const handleUndoCheckIn = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => undoCheckInDialog.value.open() })
  } else {
    undoCheckInDialog.value.open()
  }
}

const handleCancel = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => cancelDialog.value.open() })
  } else {
    cancelDialog.value.open()
  }
}

const handleUndoCancel = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => undoCancelDialog.value.open() })
  } else {
    undoCancelDialog.value.open()
  }
}

const handleDelete = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => deleteDialog.value.open() })
  } else {
    deleteDialog.value.open()
  }
}

const handleConfirm = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => confirmDialog.value.open() })
  } else {
    confirmDialog.value.open()
  }
}

const handleRecurring = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => recurrenceModal.value.open(reservation.value) })
  } else {
    recurrenceModal.value.open(reservation.value)
  }
}

const handleNoShow = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => noShowDialog.value.open() })
  } else {
    noShowDialog.value.open()
  }
}

const handleUndoNoShow = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => undoNoShowDialog.value.open() })
  } else {
    undoNoShowDialog.value.open()
  }
}

const handleSendEmail = (type: any) => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => sendEmailDialog.value.open(type) })
  } else {
    sendEmailDialog.value.open(type)
  }
}

const handlePrint = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => printDialog.value.open(reservation.value) })
  } else {
    printDialog.value.open(reservation.value)
  }
}

const handleRestore = async () => {
  try {
    loading.value = true

    const response = await $api.reservation.restore(reservation.value)

    if (response) {
      $notification.toast({
        title: $i18n.t('reservation.restoredSuccess'),
        text: $i18n.t('reservation.restoredSuccessInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Reservation', reservation.value)
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const handleCreateOrder = async () => {
  emit('update:loading', true)

  const customer = reservation.value.customer
  const items = [reservation.value]

  await createOrder({ items, customer })

  emit('update:loading', false)
}

const callIntegrationAction = async (integration: any, action: any) => {
  if (action.isCallback) {
    emit('update:loading', true)

    try {
      const date = (reservation.value.date as any)?.integer ?? reservation.value.date
      const r = (await $api.reservation.integrationAction(
        reservation.value.idReservation.toString(),
        date,
        { guid: integration.guid, actionCode: action.code }
      )).data

      reservation.value = r

      if (r) {
        emit('update:reservation', r)
        emit('update:loading', false)
      }
    } catch (e) {
      console.log(e)
    }
  } else if (action.isInternalUrl) {
    // open internal URL
  } else if (action.isExternalUrl) {
    window.open(action.url)
  }
}

const handleUpdate = async () => {
  await fetchReservation(componentProps.id, componentProps.date)

  if (reservation.value) {
    $bus.emit.updated('Reservation', reservation.value, uid)
  }
}

const handleFocused = () => {
  const m = moved.value
  const f = componentProps.focused

  if (m && f) {
    setTimeout(() => {
      movedDialog.value.open()

      moved.value = false
    }, 500)
  }
}

const handleMove = () => {
  panel.replace('appointment/[id]', {
    id: String(moved.value.idReservation),
    date: String(moved.value.date.value),
  })

  moved.value = false
}

const handleRebook = () => {
  if (unsavedChanges.value) {
    unsavedChangesDialog.value.open({ callback: () => rebookDialog.value.open() })
  } else {
    rebookDialog.value.open()
  }
}

const handleAddCustomer = () => {
  addAttendeeDialog.value.open()
}

const fetchReservation = async (idReservation?: string, date?: string) => {
  loading.value = true

  try {
    if (idReservation && date) {
      const r = (await $api.reservation.getWithDate(idReservation, date)).data
      reservation.value = r
      snapshot()
    }
  } catch (e) {
    console.log(e)
    loadError.value = true
  }

  loading.value = false
}

function isAvailabilityError (e: any): boolean {
  const errors = e?.errors
  if (!errors) return false

  const values = Array.isArray(errors) ? errors : Object.values(errors)
  return values.some((entry: any) => {
    const value = entry?.message ?? entry?.code ?? entry
    return typeof value === 'string' && value.includes('appointment_unavailable')
  })
}

async function save (close = false, future = false, overrideTime = false) {
  loading.value = true
  errors.value = false

  try {
    if (reservation.value.customer && reservation.value.customer.isNewRecord) {
      const customer = (await $api.customer.save(reservation.value.customer)).data

      if (!customer) {
        loading.value = false
        return false
      }

      reservation.value.customer = customer
    }

    const r = (await $api.reservation.saveReservation(reservation.value, { future, overrideTime })).data

    if (r) {
      reservation.value = r
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.appointment.id.success'),
        text: $i18n.t('pages.appointment.id.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Reservation', r, uid)

      if (close) {
        panel.close()
      }

      loading.value = false

      return true
    }
  } catch (e: any) {
    if (!overrideTime && isAvailabilityError(e)) {
      pendingSave.value = { close, future }
      loading.value = false
      overrideAvailabilityDialog.value.open()
      return false
    }

    $notification.error({ errors: e?.errors })
    console.log(e)
    loading.value = false
    return false
  }

  loading.value = false
}

function handleOverrideAvailability () {
  const pending = pendingSave.value
  pendingSave.value = null

  if (pending) {
    save(pending.close, pending.future, true)
  }
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    unsavedDialog.value.open()
  } else {
    panel.close(force)
  }
}

watch(
  () => [componentProps.id, componentProps.date],
  ([id, date]) => fetchReservation(id, date),
  { immediate: true },
)

$bus.on.updated('ReservationCustomer', uid, (reservationCustomer: any, source: any) => {
  if (source === uid) { return }

  const isCurrent = reservation.value?.idReservation === reservationCustomer.reservation?.idReservation

  if (isCurrent) {
    fetchReservation(componentProps.id, componentProps.date)
    $bus.emit.updated('Reservation', reservation.value, uid)
  } else {
    moved.value = reservationCustomer.reservation
    $bus.emit.updated('Reservation', reservation.value, uid)
  }
})

$bus.on.updated('Reservation', uid, (r: any, source: any) => {
  if (source === uid) { return }

  if (r?.idReservation === reservation.value?.idReservation) {
    fetchReservation(componentProps.id, componentProps.date)
  }
})

$bus.on.all('Order', uid, (order: any, source: any) => {
  if (source === uid) { return }

  const reservations = order?.data?.items?.reservations

  if (reservations) {
    if (reservations.some((r: any) => Number(r?.idReservation) === Number(reservation.value?.idReservation))) {
      fetchReservation(componentProps.id, componentProps.date)
      $bus.emit.updated('Reservation', reservation.value, uid)
    }
  }
})

onBeforeUnmount(() => {
  $bus.off.all(uid)
})
</script>
